/**
 * API service for Profile Picture Manager feature.
 *
 * Provides methods to interact with the profile pictures backend API.
 */

import axios, { AxiosInstance } from 'axios';
import {
  UserPhotoStatusList,
  PhotoListResponse,
  PhotoUploadResponse,
  SetPrimaryPhotoRequest,
  SyncStatusResponse,
  UserFilters,
  PaginationParams,
  PhotoUploadJobResponse,
  PhotoUploadJobStatus,
} from '../types';

class ProfilePictureApiService {
  private api: AxiosInstance;

  constructor() {
    this.api = axios.create({
      baseURL: process.env.REACT_APP_API_URL || 'http://localhost:9110',
      timeout: 60000,
    });

    this.setupInterceptors();
  }

  private setupInterceptors() {
    // Request interceptor
    this.api.interceptors.request.use(
      (config) => {
        // Don't set Content-Type for FormData - browser will set it with boundary
        if (config.data instanceof FormData && config.headers) {
          const headers = config.headers as Record<string, unknown> & { common?: Record<string, unknown> };
          delete headers['Content-Type'];
          delete headers['content-type'];
          if (headers.common) {
            delete headers.common['Content-Type'];
            delete headers.common['content-type'];
          }
        }
        return config;
      },
      (error) => {
        console.error('API Request Error:', error);
        return Promise.reject(error);
      }
    );

    // Response interceptor
    this.api.interceptors.response.use(
      (response) => response,
      (error) => {
        console.error('API Response Error:', error.response?.data || error.message);
        return Promise.reject(error);
      }
    );
  }

  /**
   * Set authorization token for API requests.
   */
  setToken(token: string) {
    this.api.defaults.headers.common['Authorization'] = `Bearer ${token}`;
  }

  async searchConsentUsers(query: string, token: string): Promise<Array<{ user_id: string; display_name: string; email: string }>> {
    this.setToken(token);
    const response = await this.api.get('/api/proposal/admin/consent/users', { params: { q: query } });
    return response.data.items;
  }

  async getConsentStatus(userId: string, token: string): Promise<{ consented: boolean; withdrawn_at: string | null }> {
    this.setToken(token);
    const response = await this.api.get(`/api/proposal/admin/consent/${encodeURIComponent(userId)}`);
    return response.data;
  }

  async updateConsent(userId: string, action: 'withdraw' | 'revert', source: string, token: string): Promise<{ consented: boolean; withdrawn_at: string | null }> {
    this.setToken(token);
    const response = await this.api.post(`/api/proposal/admin/consent/${encodeURIComponent(userId)}/${action}`, { source });
    return response.data;
  }

  /**
   * Get paginated list of users with photo status.
   */
  async getUsersWithPhotoStatus(
    filters: UserFilters,
    pagination: PaginationParams,
    token: string
  ): Promise<UserPhotoStatusList> {
    this.setToken(token);

    const params: Record<string, string | number | boolean> = {
      limit: pagination.limit,
      offset: pagination.offset,
    };

    if (filters.search) {
      params.q = filters.search;
    }
    if (filters.department) {
      params.department = filters.department;
    }
    if (filters.hasPhoto !== undefined) {
      params.has_photo = filters.hasPhoto;
    }

    const response = await this.api.get<UserPhotoStatusList>('/api/profile-pictures/users', {
      params,
    });
    return response.data;
  }

  /**
   * Get user's current profile picture from Microsoft Graph.
   * Returns a blob URL for displaying the image.
   */
  async getUserGraphPhoto(userId: string, token: string): Promise<string | null> {
    this.setToken(token);

    try {
      const response = await this.api.get(`/api/profile-pictures/users/${userId}/graph-photo`, {
        responseType: 'blob',
      });

      // Create blob URL
      const blob = new Blob([response.data], { type: 'image/jpeg' });
      return URL.createObjectURL(blob);
    } catch (error: any) {
      if (error.response?.status === 404) {
        return null; // No photo found
      }
      throw error;
    }
  }

  /**
   * Get profile pictures for multiple users in a single batch request.
   * Returns a Map of userId -> blob URL.
   * Dramatically reduces the number of API calls (N -> 1).
   */
  async getBatchUserPhotos(userIds: string[], token: string): Promise<Map<string, string>> {
    this.setToken(token);

    try {
      // Join user IDs as comma-separated string
      const userIdsParam = userIds.join(',');

      const response = await this.api.get<Record<string, string | null>>(
        '/api/profile-pictures/users/batch-photos',
        {
          params: { user_ids: userIdsParam }
        }
      );

      // Convert base64 photos to blob URLs
      const photoMap = new Map<string, string>();

      for (const [userId, base64Photo] of Object.entries(response.data)) {
        if (base64Photo) {
          // Decode base64 to binary
          const binaryString = atob(base64Photo);
          const bytes = new Uint8Array(binaryString.length);
          for (let i = 0; i < binaryString.length; i++) {
            bytes[i] = binaryString.charCodeAt(i);
          }

          // Create blob URL
          const blob = new Blob([bytes], { type: 'image/jpeg' });
          const url = URL.createObjectURL(blob);
          photoMap.set(userId, url);
        }
      }

      return photoMap;
    } catch (error: any) {
      console.error('Error fetching batch photos:', error);
      // Return empty map on error - UI will show placeholders
      return new Map();
    }
  }

  /**
   * Get photo content from SharePoint by photo ID.
   * Returns a blob URL for displaying the image.
   */
  async getPhotoContent(photoId: string, token: string): Promise<string | null> {
    this.setToken(token);

    try {
      const response = await this.api.get(`/api/profile-pictures/photos/${photoId}/content`, {
        responseType: 'blob',
      });

      // Create blob URL
      const blob = new Blob([response.data], { type: 'image/jpeg' });
      return URL.createObjectURL(blob);
    } catch (error: any) {
      if (error.response?.status === 404) {
        return null; // No photo found
      }
      throw error;
    }
  }

  /**
   * Get all photos for a specific user.
   */
  async getUserPhotos(userId: string, token: string): Promise<PhotoListResponse> {
    this.setToken(token);

    const response = await this.api.get<PhotoListResponse>(
      `/api/profile-pictures/users/${userId}/photos`,
      {
        params: {
          _t: Date.now() // Cache-busting parameter
        }
      }
    );
    return response.data;
  }

  /**
   * Upload a new photo for a user to SharePoint.
   */
  async uploadUserPhoto(
    userId: string,
    file: File,
    token: string
  ): Promise<PhotoUploadResponse> {
    const startTime = performance.now();
    console.log('[API] 🚀 Starting HTTP POST for photo upload, file size:', (file.size / 1024).toFixed(0), 'KB');

    this.setToken(token);

    const formDataStartTime = performance.now();
    const formData = new FormData();
    formData.append('file', file);
    console.log(`[API] 📋 FormData created (+${(formDataStartTime - startTime).toFixed(0)}ms)`);

    const requestStartTime = performance.now();
    const response = await this.api.post<PhotoUploadResponse>(
      `/api/profile-pictures/users/${userId}/photos`,
      formData
    );
    const requestEndTime = performance.now();

    console.log(`[API] ✅ HTTP POST completed (+${(requestEndTime - requestStartTime).toFixed(0)}ms)`);
    console.log(`[API] ⏱️ Total API call time: ${(requestEndTime - startTime).toFixed(0)}ms`);

    return response.data;
  }

  /**
   * Upload a new photo for a user to SharePoint (async/queued).
   * Returns immediately with a job ID for tracking progress.
   */
  async uploadUserPhotoAsync(
    userId: string,
    file: File,
    token: string
  ): Promise<PhotoUploadJobResponse> {
    const startTime = performance.now();
    console.log('[API] 🚀 Starting async photo upload, file size:', (file.size / 1024).toFixed(0), 'KB');

    this.setToken(token);

    const formData = new FormData();
    formData.append('file', file);

    const requestStartTime = performance.now();
    const response = await this.api.post<PhotoUploadJobResponse>(
      `/api/profile-pictures/users/${userId}/photos/upload-async`,
      formData
    );
    const requestEndTime = performance.now();

    console.log(`[API] ✅ Async upload queued (+${(requestEndTime - requestStartTime).toFixed(0)}ms)`);
    console.log(`[API] ⏱️ Total API call time: ${(requestEndTime - startTime).toFixed(0)}ms`);

    return response.data;
  }

  /**
   * Get status of an async photo upload job.
   */
  async getUploadStatus(jobId: string, token: string): Promise<PhotoUploadJobStatus> {
    this.setToken(token);

    const response = await this.api.get<PhotoUploadJobStatus>(
      `/api/profile-pictures/upload-status/${jobId}`
    );
    return response.data;
  }

  /**
   * Associate an existing SharePoint photo with a user.
   */
  async associateExistingPhoto(
    userId: string,
    sharepointItemId: string,
    token: string
  ): Promise<{ id: string; message: string }> {
    this.setToken(token);

    const response = await this.api.post(
      `/api/profile-pictures/users/${userId}/photos/associate`,
      { sharepoint_item_id: sharepointItemId }
    );
    return response.data;
  }

  /**
   * Delete a photo association (and optionally the file from SharePoint).
   */
  async deleteUserPhoto(
    userId: string,
    photoId: string,
    deleteFile: boolean,
    token: string
  ): Promise<{ message: string }> {
    this.setToken(token);

    const response = await this.api.delete(
      `/api/profile-pictures/users/${userId}/photos/${photoId}`,
      {
        params: { delete_file: deleteFile },
      }
    );
    return response.data;
  }

  /**
   * Set a photo as primary and sync to Microsoft Graph.
   */
  async setPrimaryPhoto(
    userId: string,
    request: SetPrimaryPhotoRequest,
    token: string
  ): Promise<{ message: string; photo_id: string }> {
    this.setToken(token);

    const response = await this.api.post(
      `/api/profile-pictures/users/${userId}/set-primary`,
      request
    );
    return response.data;
  }

  /**
   * Clear photo from Microsoft Graph (keep SharePoint associations).
   */
  async clearGraphPhoto(userId: string, token: string): Promise<{ message: string }> {
    this.setToken(token);

    const response = await this.api.post(
      `/api/profile-pictures/users/${userId}/clear-graph-photo`
    );
    return response.data;
  }

  /**
   * Get sync status for a user's photos.
   */
  async getSyncStatus(userId: string, token: string): Promise<SyncStatusResponse> {
    this.setToken(token);

    const response = await this.api.get<SyncStatusResponse>(
      `/api/profile-pictures/users/${userId}/sync-status`
    );
    return response.data;
  }
}

// Export singleton instance
export const profilePictureApi = new ProfilePictureApiService();
